import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import LogoutIcon from "@mui/icons-material/Logout";
import RemoveIcon from "@mui/icons-material/Remove";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";

const API_BASE =
  process.env.REACT_APP_API_BASE || "https://panel.haymestasarim.com/api";

const TOKEN_KEY = "mesyat_garson_token";
const KURUM_KEY = "mesyat_garson_kurum";

const BRAND = {
  navy: "#0f172a",
  blue: "#2563eb",
  blueSoft: "#eff6ff",
  orange: "#f97316",
  orangeDark: "#c2410c",
  orangeSoft: "#fff7ed",
  green: "#16a34a",
  red: "#dc2626",
  surface: "#f8fafc",
};

function queryKurumCode() {
  try {
    const query = String(window.location.hash || "").split("?")[1] || "";
    return new URLSearchParams(query).get("kurum") || "";
  } catch {
    return "";
  }
}

function safeArray(value) {
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function currency(value) {
  const code = String(value || "TRY").toUpperCase();
  return code === "TL" ? "TRY" : code;
}

function money(value, code = "TRY") {
  return `${safeNumber(value).toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency(code)}`;
}

function normTable(value) {
  return String(value || "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/masası|masa|table|tisch|nolu|numara|nummer|\bno\b/g, "")
    .replace(/\s+/g, "")
    .replace(/[^a-z0-9ığüşöç]/g, "")
    .replace(/\d+/g, (part) => String(Number(part)));
}

function itemTitle(item) {
  return item?.display_title || item?.title || item?.urun_adi || item?.name || "Ürün";
}

function itemQuantity(item) {
  return Math.max(1, safeNumber(item?.quantity || item?.adet || 1));
}

function basketKey(item) {
  return String(item.basket_key || `${item.source}-${item.id}-${item.option_id || "base"}`);
}

function requestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `garson-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function apiRequest(path, options = {}, token = "") {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || json?.ok === false) {
    const error = new Error(json?.message || "İşlem tamamlanamadı");
    error.status = response.status;
    throw error;
  }
  return json;
}

function LoginView({ loading, onLogin }) {
  const [kurumKodu, setKurumKodu] = useState(
    queryKurumCode() || localStorage.getItem(KURUM_KEY) || ""
  );
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (!kurumKodu.trim() || !/^\d{4,6}$/.test(pin)) {
      setError("Kurum kodunu ve 4–6 haneli PIN'inizi girin.");
      return;
    }
    try {
      await onLogin(kurumKodu.trim(), pin);
    } catch (loginError) {
      setError(loginError.message || "Giriş yapılamadı");
      setPin("");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        p: 2,
        background:
          "radial-gradient(circle at 10% 10%,rgba(37,99,235,.20),transparent 32%), radial-gradient(circle at 90% 90%,rgba(249,115,22,.22),transparent 34%), #f8fafc",
      }}
    >
      <Paper
        component="form"
        onSubmit={submit}
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 430,
          p: { xs: 2.5, sm: 4 },
          borderRadius: 5,
          border: "1px solid #e2e8f0",
          boxShadow: "0 30px 80px rgba(15,23,42,.14)",
        }}
      >
        <Stack spacing={2.2}>
          <Stack alignItems="center" spacing={1}>
            <Avatar
              sx={{
                width: 72,
                height: 72,
                bgcolor: BRAND.navy,
                border: `5px solid ${BRAND.orangeSoft}`,
              }}
            >
              <RestaurantMenuIcon sx={{ fontSize: 34 }} />
            </Avatar>
            <Typography variant="h4" sx={{ fontWeight: 950, color: BRAND.navy }}>
              MesYat Garson
            </Typography>
            <Typography color="text.secondary" align="center" sx={{ fontWeight: 650 }}>
              Salon, masa ve sipariş işlemlerine güvenli personel girişi
            </Typography>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            autoFocus
            label="Kurum kodu"
            value={kurumKodu}
            onChange={(event) => setKurumKodu(event.target.value)}
            inputProps={{ maxLength: 80 }}
            fullWidth
          />
          <TextField
            label="Garson PIN"
            type="password"
            value={pin}
            onChange={(event) =>
              setPin(event.target.value.replace(/\D/g, "").slice(0, 6))
            }
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            fullWidth
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              minHeight: 54,
              borderRadius: 3,
              bgcolor: BRAND.orange,
              fontWeight: 950,
              textTransform: "none",
              "&:hover": { bgcolor: BRAND.orangeDark },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Garson Girişi"}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

export default function GarsonSiparisPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedSalonId, setSelectedSalonId] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [catalogMode, setCatalogMode] = useState("menu");
  const [selectedCategoryId, setSelectedCategoryId] = useState("all");
  const [productSearch, setProductSearch] = useState("");
  const [basket, setBasket] = useState([]);
  const [orderNote, setOrderNote] = useState("");
  const [optionProduct, setOptionProduct] = useState(null);
  const [customDialog, setCustomDialog] = useState(false);
  const [customForm, setCustomForm] = useState({ title: "", price: "", quantity: 1 });
  const [sending, setSending] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const notify = useCallback((message, severity = "success") => {
    setSnack({ open: true, severity, message });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
    setSelectedTableId(null);
    setBasket([]);
  }, []);

  const loadData = useCallback(
    async (silent = false) => {
      if (!token) return;
      silent ? setRefreshing(true) : setLoading(true);
      try {
        const json = await apiRequest("/garson/bootstrap?lang=tr", {}, token);
        setData(json.data || null);
      } catch (error) {
        if ([401, 403].includes(error.status)) logout();
        else if (!silent) notify(error.message, "error");
      } finally {
        silent ? setRefreshing(false) : setLoading(false);
      }
    },
    [logout, notify, token]
  );

  useEffect(() => {
    if (!token) return undefined;
    loadData(false);
    const timer = window.setInterval(() => loadData(true), 7000);
    return () => window.clearInterval(timer);
  }, [loadData, token]);

  const login = async (kurumKodu, pin) => {
    setLoading(true);
    try {
      const json = await apiRequest("/garson/login", {
        method: "POST",
        body: JSON.stringify({ kurum_kodu: kurumKodu, pin }),
      });
      localStorage.setItem(TOKEN_KEY, json.token);
      localStorage.setItem(KURUM_KEY, kurumKodu);
      setToken(json.token);
    } finally {
      setLoading(false);
    }
  };

  const salons = data?.salons || [];
  const tables = data?.tables || [];
  const orders = data?.orders || [];
  const payments = data?.payments || [];
  const profile = data?.profile || {};

  const tableStats = useMemo(() => {
    const map = {};
    for (const table of tables) {
      const keys = new Set([table.id, normTable(table.masa_no), normTable(table.name)].map(String));
      const belongs = (row) =>
        (row?.masa_id && String(row.masa_id) === String(table.id)) ||
        keys.has(String(normTable(row?.masa_no || row?.masa || row?.table_no)));
      const tableOrders = orders.filter(
        (order) => belongs(order) && String(order.durum || "") !== "iptal"
      );
      const tablePayments = payments.filter((payment) => belongs(payment));
      const total = tableOrders.reduce(
        (sum, order) => sum + safeNumber(order.toplam_tutar || order.total_amount),
        0
      );
      const paid = tablePayments
        .filter((payment) => ["odendi", "onaylandi"].includes(String(payment.status)))
        .reduce((sum, payment) => sum + safeNumber(payment.amount), 0);
      const pending = tablePayments.some((payment) => payment.status === "bekliyor");
      map[table.id] = {
        orders: tableOrders,
        total,
        paid,
        remaining: Math.max(total - paid, 0),
        status: pending ? "payment" : total > paid ? "active" : table.status || "bos",
      };
    }
    return map;
  }, [orders, payments, tables]);

  const selectedTable = tables.find((table) => String(table.id) === String(selectedTableId));
  const selectedStats = selectedTable ? tableStats[selectedTable.id] : null;

  const filteredTables = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("tr-TR");
    return tables.filter((table) => {
      if (selectedSalonId !== "all" && String(table.salon_id) !== String(selectedSalonId)) {
        return false;
      }
      if (!query) return true;
      const salon = salons.find((item) => String(item.id) === String(table.salon_id));
      return `${table.name} ${table.masa_no} ${salon?.name || ""}`
        .toLocaleLowerCase("tr-TR")
        .includes(query);
    });
  }, [salons, search, selectedSalonId, tables]);

  const categories = useMemo(
    () =>
      (data?.menu || []).flatMap((section) =>
        (section.categories || []).map((category) => ({
          ...category,
          section_title: section.title,
        }))
      ),
    [data?.menu]
  );

  const menuItems = useMemo(() => {
    const query = productSearch.trim().toLocaleLowerCase("tr-TR");
    return categories
      .filter(
        (category) =>
          selectedCategoryId === "all" || String(category.id) === String(selectedCategoryId)
      )
      .flatMap((category) =>
        (category.items || []).map((item) => ({ ...item, category_title: category.title }))
      )
      .filter((item) =>
        query
          ? `${item.title} ${item.description || ""} ${item.category_title || ""}`
              .toLocaleLowerCase("tr-TR")
              .includes(query)
          : true
      );
  }, [categories, productSearch, selectedCategoryId]);

  const stockItems = useMemo(() => {
    const query = productSearch.trim().toLocaleLowerCase("tr-TR");
    return (data?.stocks || []).filter((item) =>
      query ? String(item.title).toLocaleLowerCase("tr-TR").includes(query) : true
    );
  }, [data?.stocks, productSearch]);

  const addBasketItem = (product, source, option = null) => {
    const requestedCurrency = currency(option?.currency || product.currency);
    const currentCurrency = basket[0]?.currency
      ? currency(basket[0].currency)
      : null;
    if (currentCurrency && currentCurrency !== requestedCurrency) {
      notify(
        `Sepette ${currentCurrency} ürün bulunuyor. Farklı para birimleri aynı siparişe eklenemez.`,
        "warning"
      );
      return;
    }

    const item = {
      basket_key: `${source}-${product.id}-${option?.id || "base"}`,
      id: product.id,
      menu_item_id: source === "menu" ? product.id : null,
      stock_item_id: source === "stock" ? product.id : null,
      source,
      item_source: source,
      title: product.title,
      display_title: option?.label ? `${product.title} - ${option.label}` : product.title,
      option_id: option?.id || null,
      option_label: option?.label || "",
      price: safeNumber(option?.price ?? product.price),
      currency: requestedCurrency,
      quantity: 1,
      note: "",
    };
    setBasket((previous) => {
      const found = previous.find((row) => basketKey(row) === basketKey(item));
      return found
        ? previous.map((row) =>
            basketKey(row) === basketKey(item)
              ? { ...row, quantity: itemQuantity(row) + 1 }
              : row
          )
        : [...previous, item];
    });
    setOptionProduct(null);
  };

  const selectProduct = (product, source) => {
    const options = safeArray(product.price_options);
    if (source === "menu" && options.length) setOptionProduct(product);
    else addBasketItem(product, source);
  };

  const changeQuantity = (key, delta) => {
    setBasket((previous) =>
      previous
        .map((item) =>
          basketKey(item) === key
            ? { ...item, quantity: Math.max(0, itemQuantity(item) + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const addCustom = () => {
    const title = customForm.title.trim();
    const price = safeNumber(String(customForm.price).replace(",", "."));
    if (!title || price <= 0) {
      notify("Özel ürün adı ve fiyatı zorunludur", "warning");
      return;
    }
    if (basket[0]?.currency && currency(basket[0].currency) !== "TRY") {
      notify("Özel ürün TRY olarak eklenir; sepette farklı para birimi bulunuyor.", "warning");
      return;
    }
    const item = {
      basket_key: `custom-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      source: "custom",
      item_source: "custom",
      title,
      display_title: title,
      price,
      currency: "TRY",
      quantity: Math.max(1, Number(customForm.quantity || 1)),
    };
    setBasket((previous) => [...previous, item]);
    setCustomForm({ title: "", price: "", quantity: 1 });
    setCustomDialog(false);
  };

  const basketTotal = basket.reduce(
    (sum, item) => sum + safeNumber(item.price) * itemQuantity(item),
    0
  );

  const sendOrder = async () => {
    if (!selectedTable || !basket.length) return;
    setSending(true);
    try {
      const json = await apiRequest(
        `/garson/masalar/${selectedTable.id}/siparis`,
        {
          method: "POST",
          body: JSON.stringify({
            client_request_id: requestId(),
            menu_language: "tr",
            note: orderNote.trim(),
            items: basket,
          }),
        },
        token
      );
      setBasket([]);
      setOrderNote("");
      notify(json.message || "Sipariş mutfağa ve kasaya gönderildi");
      await loadData(true);
    } catch (error) {
      notify(error.message || "Sipariş gönderilemedi", "error");
    } finally {
      setSending(false);
    }
  };

  const openTable = (tableId) => {
    setSelectedTableId(tableId);
    setCatalogMode("menu");
    setSelectedCategoryId("all");
    setProductSearch("");
    setBasket([]);
    setOrderNote("");
  };

  if (!token) return <LoginView loading={loading} onLogin={login} />;

  if (loading && !data) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center" }}>
        <Stack alignItems="center" spacing={1.5}>
          <CircularProgress sx={{ color: BRAND.orange }} />
          <Typography sx={{ fontWeight: 850 }}>Garson ekranı hazırlanıyor…</Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: BRAND.surface }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: BRAND.navy }}>
        <Toolbar sx={{ gap: 1.2 }}>
          <Avatar sx={{ bgcolor: BRAND.orange, width: 38, height: 38 }}>
            {String(profile.ad_soyad || "G").slice(0, 1).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, lineHeight: 1.1 }}>MesYat Garson</Typography>
            <Typography variant="caption" sx={{ opacity: 0.78, fontWeight: 700 }} noWrap>
              {profile.ad_soyad || "Personel"} {refreshing ? "· Güncelleniyor" : "· Canlı"}
            </Typography>
          </Box>
          {profile.mutfak_gorebilir && (
            <IconButton
              color="inherit"
              aria-label="Mutfak ve yazıcı ekranı"
              onClick={() => {
                window.location.hash = "/mutfak-yazici";
              }}
            >
              <LocalPrintshopIcon />
            </IconButton>
          )}
          <IconButton color="inherit" onClick={logout} aria-label="Çıkış">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ width: "100%", maxWidth: 1500, mx: "auto", p: { xs: 1.2, sm: 2.2 } }}>
        <Paper
          elevation={0}
          sx={{ p: { xs: 1.2, sm: 1.6 }, borderRadius: 4, border: "1px solid #e2e8f0", mb: 1.6 }}
        >
          <Stack spacing={1.2}>
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Masa veya salon ara"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                ),
              }}
            />
            <Box sx={{ overflowX: "auto", pb: 0.3 }}>
              <ToggleButtonGroup
                exclusive
                value={selectedSalonId}
                onChange={(_event, value) => value && setSelectedSalonId(value)}
                sx={{ minWidth: "max-content" }}
              >
                <ToggleButton value="all" sx={{ fontWeight: 900 }}>Tüm Alanlar</ToggleButton>
                {salons.map((salon) => (
                  <ToggleButton key={salon.id} value={salon.id} sx={{ fontWeight: 900 }}>
                    {salon.name}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Stack>
        </Paper>

        {!filteredTables.length ? (
          <Alert severity="info">Bu alanda masa bulunamadı.</Alert>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))", lg: "repeat(5,minmax(0,1fr))" },
              gap: { xs: 1, sm: 1.5 },
            }}
          >
            {filteredTables.map((table) => {
              const stat = tableStats[table.id] || {};
              const salon = salons.find((item) => String(item.id) === String(table.salon_id));
              const active = ["active", "payment", "dolu", "aktif", "odeme"].includes(stat.status);
              return (
                <Card
                  key={table.id}
                  elevation={0}
                  sx={{
                    borderRadius: 3.5,
                    border: `1px solid ${active ? alpha(BRAND.orange, 0.45) : "#e2e8f0"}`,
                    overflow: "hidden",
                    bgcolor: active ? BRAND.orangeSoft : "#fff",
                  }}
                >
                  <Box sx={{ height: 5, bgcolor: salon?.color || BRAND.blue }} />
                  <CardActionArea onClick={() => openTable(table.id)} sx={{ height: "100%" }}>
                    <CardContent sx={{ p: { xs: 1.25, sm: 1.7 } }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.5}>
                          <Typography sx={{ fontWeight: 950, color: BRAND.navy, fontSize: { xs: 16, sm: 18 } }}>
                            {table.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={stat.status === "payment" ? "Ödeme" : active ? "Dolu" : "Boş"}
                            color={stat.status === "payment" ? "error" : active ? "warning" : "default"}
                            sx={{ fontWeight: 900 }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>
                          {salon?.name || "Salon"} · {table.capacity} kişi
                        </Typography>
                        <Divider />
                        <Typography sx={{ fontWeight: 950, color: active ? BRAND.orangeDark : BRAND.navy }}>
                          {money(stat.total || 0, stat.orders?.[0]?.currency)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 750 }}>
                          {stat.orders?.length || 0} sipariş
                        </Typography>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>

      <Dialog
        open={Boolean(selectedTable)}
        onClose={() => !sending && setSelectedTableId(null)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="xl"
        PaperProps={{ sx: { borderRadius: { xs: 0, md: 4 }, overflow: "hidden" } }}
      >
        {selectedTable && (
          <>
            <DialogTitle sx={{ p: 0, bgcolor: BRAND.navy, color: "#fff" }}>
              <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
                <IconButton color="inherit" onClick={() => setSelectedTableId(null)}>
                  <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0, ml: 1 }}>
                  <Typography sx={{ fontWeight: 950 }} noWrap>{selectedTable.name}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    Açık hesap: {money(selectedStats?.total || 0)}
                  </Typography>
                </Box>
                <Chip label={`${basket.length} kalem`} sx={{ bgcolor: "#fff", fontWeight: 900 }} />
              </Toolbar>
            </DialogTitle>

            <DialogContent sx={{ p: 0, bgcolor: BRAND.surface, overflow: "hidden" }}>
              <Box
                sx={{
                  height: "100%",
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "minmax(0,1fr) 380px" },
                  minHeight: 0,
                }}
              >
                <Box sx={{ minWidth: 0, overflowY: "auto", p: { xs: 1.2, sm: 2 } }}>
                  <Stack spacing={1.3}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <ToggleButtonGroup
                        exclusive
                        value={catalogMode}
                        onChange={(_event, value) => value && setCatalogMode(value)}
                        fullWidth
                      >
                        <ToggleButton value="menu" sx={{ fontWeight: 900 }}>
                          <RestaurantMenuIcon sx={{ mr: 0.7 }} /> Menü
                        </ToggleButton>
                        <ToggleButton value="stock" sx={{ fontWeight: 900 }}>
                          <Inventory2Icon sx={{ mr: 0.7 }} /> Stok
                        </ToggleButton>
                      </ToggleButtonGroup>
                      {profile.ozel_urun_ekleyebilir && (
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={() => setCustomDialog(true)}
                          sx={{ minWidth: 150, fontWeight: 900, textTransform: "none" }}
                        >
                          Özel Ürün
                        </Button>
                      )}
                    </Stack>

                    <TextField
                      value={productSearch}
                      onChange={(event) => setProductSearch(event.target.value)}
                      placeholder="Ürün ara"
                      size="small"
                      fullWidth
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                    />

                    {catalogMode === "menu" && (
                      <Box sx={{ overflowX: "auto", pb: 0.3 }}>
                        <Stack direction="row" spacing={0.7} sx={{ minWidth: "max-content" }}>
                          <Chip
                            clickable
                            color={selectedCategoryId === "all" ? "primary" : "default"}
                            label="Tümü"
                            onClick={() => setSelectedCategoryId("all")}
                            sx={{ fontWeight: 850 }}
                          />
                          {categories.map((category) => (
                            <Chip
                              key={category.id}
                              clickable
                              color={String(selectedCategoryId) === String(category.id) ? "primary" : "default"}
                              label={category.title}
                              onClick={() => setSelectedCategoryId(category.id)}
                              sx={{ fontWeight: 850 }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))", lg: "repeat(4,minmax(0,1fr))" },
                        gap: 1,
                      }}
                    >
                      {(catalogMode === "menu" ? menuItems : stockItems).map((product) => (
                        <Card key={`${catalogMode}-${product.id}`} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3 }}>
                          <CardActionArea onClick={() => selectProduct(product, catalogMode)} sx={{ height: "100%" }}>
                            <CardContent sx={{ p: 1.2 }}>
                              <Stack spacing={0.7}>
                                <Typography sx={{ fontWeight: 900, lineHeight: 1.2, minHeight: 38 }}>
                                  {product.title}
                                </Typography>
                                {catalogMode === "stock" && (
                                  <Chip size="small" label={`Stok: ${safeNumber(product.stock)}`} color={safeNumber(product.stock) <= 0 ? "error" : "default"} />
                                )}
                                <Typography sx={{ fontWeight: 950, color: BRAND.blue }}>
                                  {safeArray(product.price_options).length
                                    ? "Seçenekli"
                                    : money(product.price, product.currency)}
                                </Typography>
                                <Button size="small" variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: BRAND.orange, fontWeight: 900, textTransform: "none", boxShadow: "none" }}>
                                  Ekle
                                </Button>
                              </Stack>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      ))}
                    </Box>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    borderLeft: { md: "1px solid #e2e8f0" },
                    borderTop: { xs: "1px solid #e2e8f0", md: "none" },
                    bgcolor: "#fff",
                    p: 1.5,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    maxHeight: { xs: "52dvh", md: "none" },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 950 }}>Masa Sepeti</Typography>
                    <Chip label={`${basket.length} kalem`} sx={{ fontWeight: 850 }} />
                  </Stack>
                  <Box sx={{ flex: 1, overflowY: "auto", minHeight: 80 }}>
                    {!basket.length ? (
                      <Alert severity="info">Soldan ürün seçin.</Alert>
                    ) : (
                      <Stack spacing={0.8}>
                        {basket.map((item) => (
                          <Paper key={basketKey(item)} elevation={0} sx={{ p: 1, border: "1px solid #e2e8f0", borderRadius: 2.5 }}>
                            <Typography sx={{ fontWeight: 850, lineHeight: 1.2 }}>{itemTitle(item)}</Typography>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.7 }}>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <IconButton size="small" onClick={() => changeQuantity(basketKey(item), -1)}><RemoveIcon fontSize="small" /></IconButton>
                                <Typography sx={{ minWidth: 24, textAlign: "center", fontWeight: 950 }}>{itemQuantity(item)}</Typography>
                                <IconButton size="small" onClick={() => changeQuantity(basketKey(item), 1)}><AddIcon fontSize="small" /></IconButton>
                              </Stack>
                              <Typography sx={{ fontWeight: 950 }}>{money(item.price * itemQuantity(item), item.currency)}</Typography>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Box>
                  <TextField
                    value={orderNote}
                    onChange={(event) => setOrderNote(event.target.value)}
                    label="Sipariş notu"
                    multiline
                    minRows={2}
                    maxRows={3}
                    fullWidth
                    sx={{ mt: 1 }}
                  />
                  <Box sx={{ p: 1.4, my: 1, borderRadius: 3, bgcolor: BRAND.blueSoft }}>
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>Yeni sipariş toplamı</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.blue }}>{money(basketTotal, basket[0]?.currency)}</Typography>
                  </Box>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    disabled={!basket.length || sending}
                    onClick={sendOrder}
                    sx={{ minHeight: 54, borderRadius: 3, bgcolor: BRAND.orange, fontWeight: 950, textTransform: "none", "&:hover": { bgcolor: BRAND.orangeDark } }}
                  >
                    Mutfağa ve Kasaya Gönder
                  </Button>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog open={Boolean(optionProduct)} onClose={() => setOptionProduct(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 950 }}>{optionProduct?.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {safeArray(optionProduct?.price_options).map((option, index) => (
              <Button
                key={option.id || index}
                variant="outlined"
                onClick={() => addBasketItem(optionProduct, "menu", option)}
                sx={{ justifyContent: "space-between", minHeight: 50, fontWeight: 850, textTransform: "none" }}
              >
                <span>{option.label || option.name || `Seçenek ${index + 1}`}</span>
                <span>{money(option.price, option.currency || optionProduct?.currency)}</span>
              </Button>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOptionProduct(null)}>Vazgeç</Button></DialogActions>
      </Dialog>

      <Dialog open={customDialog} onClose={() => setCustomDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 950 }}>Özel Ürün Ekle</DialogTitle>
        <DialogContent>
          <Stack spacing={1.3} sx={{ mt: 1 }}>
            <TextField label="Ürün adı" value={customForm.title} onChange={(event) => setCustomForm((old) => ({ ...old, title: event.target.value }))} />
            <TextField label="Fiyat" type="number" value={customForm.price} onChange={(event) => setCustomForm((old) => ({ ...old, price: event.target.value }))} />
            <TextField select label="Adet" value={customForm.quantity} onChange={(event) => setCustomForm((old) => ({ ...old, quantity: Number(event.target.value) }))}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDialog(false)}>Vazgeç</Button>
          <Button variant="contained" onClick={addCustom}>Sepete Ekle</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={() => setSnack((old) => ({ ...old, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} icon={snack.severity === "success" ? <CheckCircleIcon /> : undefined} onClose={() => setSnack((old) => ({ ...old, open: false }))} sx={{ fontWeight: 850 }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
